import React, { useState, useEffect } from "react";
import itiniriLogo from "../../images/itiniri_logo_dark.png";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Dropdown from "../Dropdown/Dropdown";
import Modal from "../Modal/Modal";
import AffiliateRequestForm from "../AffiliateRequestForm/AffiliateRequestForm";

const Header = ({ title, user, signOutUser, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAffiliateModalOpen, setAffiliateModalOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSignOut = async () => {
    await signOutUser();
    navigate("/");
    setMobileMenuOpen(false);
  };

  const handleOpenAffiliateForm = () => {
    setAffiliateModalOpen(true);
    setMobileMenuOpen(false);
  };

  const handleCloseAffiliateForm = () => {
    setAffiliateModalOpen(false);
  };

  const handleHomeClick = () => {
    navigate("/");
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const dropdownOptions = [
    { label: "Account Settings", link: "/account" },
    { label: "Sign Out", onClick: handleSignOut },
    ...(isAdmin ? [{ label: "Admin View", link: "/admin" }] : []),
  ];

  return (
    <header style={styles.headerStyle}>
      <div style={styles.logoContainerStyle} onClick={() => navigate("/")}>
        <img src={itiniriLogo} alt="Site Logo" style={styles.logoStyle} />
      </div>

      {user && (
        <>
          {/* Desktop Navigation */}
          {!isMobile && (
            <nav style={styles.desktopNavStyle}>
              {(!user.referralCode || user.referralCode.trim() === "") && (
                <button
                  style={styles.buttonStyle}
                  onClick={handleOpenAffiliateForm}
                >
                  Become an Affiliate
                </button>
              )}

              {location.pathname !== "/" && (
                <button style={styles.buttonStyle} onClick={handleHomeClick}>
                  Return to Home
                </button>
              )}
              <div style={{ marginLeft: "8px" }}>
                <Dropdown options={dropdownOptions} />
              </div>
            </nav>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <div style={styles.mobileNavContainer}>
              <button
                style={styles.hamburgerStyle}
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <span
                  style={{
                    ...styles.hamburgerLineStyle,
                    transform: isMobileMenuOpen
                      ? "rotate(45deg) translate(5px, 5px)"
                      : "none",
                  }}
                ></span>
                <span
                  style={{
                    ...styles.hamburgerLineStyle,
                    opacity: isMobileMenuOpen ? "0" : "1",
                  }}
                ></span>
                <span
                  style={{
                    ...styles.hamburgerLineStyle,
                    transform: isMobileMenuOpen
                      ? "rotate(-45deg) translate(7px, -6px)"
                      : "none",
                  }}
                ></span>
              </button>

              {/* Mobile Menu Dropdown */}
              {isMobileMenuOpen && (
                <div style={styles.mobileMenuStyle}>
                  {(!user.referralCode || user.referralCode.trim() === "") && (
                    <button
                      style={styles.mobileButtonStyle}
                      onClick={handleOpenAffiliateForm}
                    >
                      Become an Affiliate
                    </button>
                  )}

                  {location.pathname !== "/" && (
                    <button
                      style={styles.mobileButtonStyle}
                      onClick={handleHomeClick}
                    >
                      Return to Home
                    </button>
                  )}

                  <Link
                    to="/account"
                    style={styles.mobileLinkStyle}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Account Settings
                  </Link>

                  {isAdmin && (
                    <Link
                      to="/admin"
                      style={styles.mobileLinkStyle}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin View
                    </Link>
                  )}

                  <button
                    style={{
                      ...styles.mobileButtonStyle,
                      ...styles.signOutButtonStyle,
                    }}
                    onClick={handleSignOut}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Overlay for mobile menu */}
          {isMobile && isMobileMenuOpen && (
            <div
              style={styles.overlayStyle}
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
        </>
      )}

      <Modal isOpen={isAffiliateModalOpen} onClose={handleCloseAffiliateForm}>
        <AffiliateRequestForm user={user} onClose={handleCloseAffiliateForm} />
      </Modal>
    </header>
  );
};

const styles = {
  headerStyle: {
    backgroundColor: "#C5EAF7",
    color: "#1a202c",
    padding: "12px 24px",
    minHeight: "70px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "2px solid rgb(186, 200, 219)",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    position: "relative",
    zIndex: 1000,
  },
  logoContainerStyle: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    transition: "transform 0.2s ease",
  },
  logoStyle: {
    width: "130px",
    cursor: "pointer",
    transition: "opacity 0.2s ease",
  },
  desktopNavStyle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  buttonStyle: {
    padding: "10px 20px",
    textDecoration: "none",
    border: "none",
    background: "linear-gradient(135deg, #2183a6 0%, #1a647f 100%)",
    color: "white",
    cursor: "pointer",
    borderRadius: "8px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: "500",
    fontSize: "0.875rem",
    boxShadow: "0 2px 4px rgba(33, 131, 166, 0.2)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  mobileNavContainer: {
    display: "flex",
    position: "relative",
  },
  hamburgerStyle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    width: "30px",
    height: "30px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "0",
    zIndex: 1001,
  },
  hamburgerLineStyle: {
    width: "30px",
    height: "3px",
    background: "#2183a6",
    borderRadius: "2px",
    transition: "all 0.3s linear",
    position: "relative",
    transformOrigin: "1px",
  },
  mobileMenuStyle: {
    position: "absolute",
    top: "45px",
    right: "0",
    background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    border: "1px solid #e2e8f0",
    padding: "16px",
    minWidth: "220px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    zIndex: 1001,
  },
  mobileButtonStyle: {
    padding: "12px 16px",
    textDecoration: "none",
    border: "none",
    background: "linear-gradient(135deg, #2183a6 0%, #1a647f 100%)",
    color: "white",
    cursor: "pointer",
    borderRadius: "8px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: "500",
    fontSize: "0.875rem",
    boxShadow: "0 2px 4px rgba(33, 131, 166, 0.2)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "block",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  mobileLinkStyle: {
    padding: "12px 16px",
    textDecoration: "none",
    border: "none",
    background: "linear-gradient(135deg, #2183a6 0%, #1a647f 100%)",
    color: "white",
    cursor: "pointer",
    borderRadius: "8px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: "500",
    fontSize: "0.875rem",
    boxShadow: "0 2px 4px rgba(33, 131, 166, 0.2)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "block",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  signOutButtonStyle: {
    background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
    boxShadow: "0 2px 4px rgba(245, 101, 101, 0.2)",
  },
  overlayStyle: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    background: "rgba(0, 0, 0, 0.5)",
    zIndex: 999,
  },
};

export default Header;
